import { useMemo } from 'react';
import { TimePicker, Input, Button } from '../../components/Common';
import CanvasJSReact from '@canvasjs/react-charts';
import InfoTooltip from '../../components/InfoTooltip/InfoTooltip';
const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const hhmmToDate = t => {
    const [h, m] = t.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
};

const dateToHHMM = d => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const hhmmToDec = t => {
    if (!t || typeof t !== 'string' || !t.includes(':')) {
        return 0;
    }
    const [h, m] = t.split(':').map(Number);
    return h + m / 60;
};

const toDate = hhmm => {
    const [h, m] = hhmm.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
};

export default function DirectScheduleConfig({ value = '[]', onChange, data, timeRange }) {
    const config = data?.config || {};
    const initialIntervals = config.intervals || [];

    const intervals = useMemo(() => {
        if (Array.isArray(initialIntervals) && initialIntervals.length > 0) {
            return initialIntervals;
        }
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }, [initialIntervals, value]);

    const update = arr => onChange(JSON.stringify(arr));

    const del = i => update(intervals.filter((_, idx) => idx !== i));

    const edit = (i, field, val) => update(intervals.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));

    const sortedIntervals = [...intervals].sort((a, b) => hhmmToDec(a.start) - hhmmToDec(b.start));

    const add = () => {
        const lastEnd = sortedIntervals.length ? sortedIntervals[sortedIntervals.length - 1].end : timeRange?.startTime;

        const defaultStart = lastEnd || timeRange?.startTime || '00:00';
        const defaultEndDec = hhmmToDec(defaultStart) + 0.5;
        const endHours = Math.floor(defaultEndDec);
        const endMinutes = Math.round((defaultEndDec % 1) * 60);
        const defaultEnd = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;

        update([...intervals, { start: defaultStart, end: defaultEnd, power_setpoint: 0 }]);
    };

    const dataPoints = intervals.flatMap(int => {
        if (!int.start || !int.end || hhmmToDec(int.start) >= hhmmToDec(int.end)) {
            return [];
        }
        const y = Number(int.power_setpoint) || 0;
        return [
            { x: hhmmToDec(int.start), y },
            { x: hhmmToDec(int.end), y },
        ];
    });

    const minX = timeRange?.startTime ? hhmmToDec(timeRange.startTime) : 0;
    const maxX = timeRange?.endTime ? hhmmToDec(timeRange.endTime) : 24;

    const chartOptions = {
        axisX: {
            minimum: minX,
            maximum: maxX,
            interval: 0.25,
            title: 'Time (h)',
            labelFormatter: function (e) {
                const hours = Math.floor(e.value);
                const minutes = Math.round((e.value % 1) * 60);
                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            },
        },
        axisY: {
            title: 'Power Setpoint (kW)',
        },
        data: [
            {
                type: 'line',
                dataPoints,
                markerSize: 6,
            },
        ],
    };

    return (
        <div className="direct-config">
            {intervals.map((int, i) => {
                const prevEnd = i === 0 ? timeRange?.startTime : intervals[i - 1]?.end || timeRange?.startTime;

                const nextStart = timeRange?.endTime;

                const minTime = prevEnd ? toDate(prevEnd) : new Date(0, 0, 0, 0, 0);
                const maxTime = nextStart ? toDate(nextStart) : new Date(0, 0, 0, 23, 59);

                const isInvalid = int.start && int.end && hhmmToDec(int.start) >= hhmmToDec(int.end);

                return (
                    <div key={i} className="direct-config__interval" style={{ marginBottom: '12px' }}>
                        <div className="direct-config__interval-input">
                            <label>Start Time:</label>
                            <TimePicker
                                value={int.start ? hhmmToDate(int.start) : null}
                                onChange={d => edit(i, 'start', dateToHHMM(d))}
                                minTime={minTime}
                                maxTime={maxTime}
                            />
                        </div>

                        <div className="direct-config__interval-input">
                            <label>End Time:</label>
                            <TimePicker
                                value={int.end ? hhmmToDate(int.end) : null}
                                onChange={d => edit(i, 'end', dateToHHMM(d))}
                                minTime={minTime}
                                maxTime={maxTime}
                            />
                        </div>

                        {isInvalid && <div style={{ color: 'red', fontSize: '12px' }}>Start time must be earlier than end time</div>}

                        <div className="direct-config__interval-input">
                            <div className="direct-config__interval-input-label">
                                <label className="direct-config__interval-input-label--text">Power Setpoint (kW):</label>
                                <InfoTooltip tooltip="Positive value means charge, while negative value means discharge." />
                            </div>
                            <Input type="number" value={int.power_setpoint} onChange={e => edit(i, 'power_setpoint', e.target.value)} />
                        </div>

                        <Button text="Delete" onClick={() => del(i)} />
                    </div>
                );
            })}

            <Button className="direct-config__interval-button" text="Add interval" onClick={add} />

            <CanvasJSChart options={chartOptions} />

            <textarea readOnly rows={3} value={JSON.stringify(intervals)} className="direct-config__json" />
        </div>
    );
}
